import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";
import Card from "../Card";
import Button from "../Button";
import Progress from "../Progress";
import { useNavigate } from "react-router-dom";
import { ReactComponent as ClipIcon } from "../assets/clip.svg";
import amazonLogo from "../assets/amazon-logo.png";


export default function PersonalQuiz() {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [scores, setScores] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [gameDetails, setGameDetails] = useState([]);
  const [characterData, setCharacterData] = useState(null);
  const [topGenre, setTopGenre] = useState(null);
  const [showFinalStep, setShowFinalStep] = useState(false);
  const navigate = useNavigate();
  const [showSharePopup, setShowSharePopup] = useState(false);


  useEffect(() => {
    const fetchGames = async () => {
      const snapshot = await getDocs(collection(db, "games"));
      const games = snapshot.docs.map((doc) => doc.data());
      setGameDetails(games);
    };

    const fetchQuestions = async () => {
      const snapshot = await getDocs(collection(db, "personal_questions"));
      const all = snapshot.docs.map((doc) => doc.data());
      const general = all.filter((q) => q.id.startsWith("general"));
      const personal = all.filter((q) => q.id.startsWith("personal"));
      const selected = [...general.slice(0, 5), ...personal.sort(() => 0.5 - Math.random()).slice(0, 30)];

      const formatted = selected.map((q) => ({
        ...q,
        options: q.choices.map((text, i) => ({
          text,
          genres: Object.keys(q.scores?.[i] || {})
        }))
      }));

      setQuestions(formatted);
    };

    fetchGames();
    fetchQuestions();
  }, []);

  useEffect(() => {
    const genreScores = {};
    gameDetails.forEach((game) => {
      const main = game.main_genre;
      const subs = game.sub_genre || [];
      genreScores[main] = (scores[main] || 0);
      for (const sub of subs) {
        genreScores[main] += (scores[sub] || 0) * 0.5;
      }
    });

    const top = Object.entries(genreScores).sort((a, b) => b[1] - a[1])[0]?.[0];
    setTopGenre(top);
  }, [scores, gameDetails]);

  useEffect(() => {
    const fetchCharacter = async () => {
      if (!topGenre) return;
      const snapshot = await getDocs(collection(db, "characters"));
      const all = snapshot.docs.map((doc) => doc.data());
      const target = all.find((c) => c.main_genre === topGenre);
      setCharacterData(target);
    };
    fetchCharacter();
  }, [topGenre]);

  const handleAnswer = (genres) => {
    const updated = [...questions];
    updated[currentQuestion].selectedGenres = genres;
    setQuestions(updated);

    setScores((prev) => {
      const next = { ...prev };
      for (const genre of genres) {
        next[genre] = (next[genre] || 0) + 1;
      }
      return next;
    });

    if (currentQuestion + 1 < questions.length) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowFinalStep(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion === 0) return;
    const prevGenres = questions[currentQuestion - 1]?.selectedGenres || [];

    setScores((prev) => {
      const next = { ...prev };
      for (const genre of prevGenres) {
        next[genre] = (next[genre] || 1) - 1;
      }
      return next;
    });

    setCurrentQuestion(currentQuestion - 1);
    setShowFinalStep(false); // ✅ ← これを追加！
  };

  const handleRetry = () => navigate("/");
  const progressValue = ((currentQuestion + 1) / questions.length) * 100;
  const filteredGames = gameDetails.filter((g) => g.main_genre === topGenre).slice(0, 3);

  useEffect(() => {
    if (showResult && topGenre) {
      navigate(`/result/${topGenre}`);
    }
  }, [showResult, topGenre, navigate]);
  

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-[#efeeea]">

      {!showResult ? (
        <Card className="w-full max-w-md p-4 shadow-lg rounded-2xl md:max-w-lg lg:max-w-xl">
          {!showFinalStep && (
            <>
            <Progress value={progressValue} />
            <h2 className="text-xl font-bold mb-4 text-center">
              {questions[currentQuestion]?.question || "Loading..."}
            </h2>
            </>
          )}

          <div className="space-y-2">
            {!showFinalStep ? (
              questions[currentQuestion]?.options?.map((option, index) => {
                const isSelected = questions[currentQuestion]?.selectedGenres?.includes(option.genres[0]);
                return (
                  <Button
                    key={index}
                    onClick={() => handleAnswer(option.genres)}
                    className={`w-full px-4 py-3 rounded-full border-2 text-center shadow-md transition font-semibold
                      ${isSelected
                        ? "bg-[#2f2301] text-white border-[#2f2301]"
                        : "bg-[#efeeea] text-[#2f2301] border-[#2f2301] hover:bg-[#2f2301] hover:text-white"}`}
                  >
                    {option.text}
                  </Button>
                );
              })
            ) : (
              <>
                <p className="text-sm text-gray-500 text-center mb-2">
                  最後の質問が完了しました
                </p>
                <div className="flex justify-between gap-2">
                  <Button className="bg-[#2f2301] text-white shadow-md rounded-full px-4 py-2" onClick={handleBack}>← 戻る</Button>
                  <Button onClick={() => setShowResult(true)} className="bg-orange-500 text-white rounded-full px-4 py-2 shadow-md">
                    診断結果を見る
                  </Button>
                </div>
              </>
            )}
          </div>
          {/* 戻る or 診断選択に戻る */}
          {!showFinalStep && (
            <div className="mt-4 text-center">
              {currentQuestion > 0 ? (
                <Button
                  onClick={handleBack}
                  className="text-sm text-[#2f2301] border border-[#2f2301] px-4 py-2 rounded-full hover:bg-[#2f2301] hover:text-white transition"
                >
                  ← 戻る
                </Button>
              ) : (
                <Button
                  onClick={() => navigate("/")}
                  className="text-sm text-[#2f2301] border border-[#2f2301] px-4 py-2 rounded-full hover:bg-[#2f2301] hover:text-white transition"
                >
                  ← 診断選択に戻る
                </Button>
              )}
            </div>
          )}

        </Card>
      ) : (
        <Card className="w-full max-w-5xl p-8 text-center shadow-lg rounded-2xl relative">
          {characterData ? (
            <>
              <div className="w-full flex flex-col items-center justify-center mb-8 relative">
                <div
                  className="text-[400px] font-bold absolute opacity-20"
                  style={{
                    color: characterData.color,
                    zIndex: 0,
                    top: "50px"
                  }}
                >
                  {characterData.main_genre}
                </div>
                <img
                  src={characterData.character_img_p}
                  alt={characterData.main_genre}
                  className="w-[650px] mb-4 relative z-10"
                  style={{
                    filter: "drop-shadow(4px 6px 6px rgba(0, 0, 0, 0.3))"
                  }}
                />
                <div className="absolute top-[50%] left-0 transform -translate-y-1/2">
                  <Button onClick={handleRetry} className="bg-[#2f2301] text-white rounded-full px-4 py-2 shadow-md">
                    もう一度診断する
                  </Button>
                </div>
                <div className="absolute top-[50%] right-0 transform -translate-y-1/2">
                <Button
                  onClick={() => setShowSharePopup(true)}
                  className="text-white rounded-full px-4 py-2 shadow-md"
                  style={{ backgroundColor: characterData.color || '#000' }}
                >
                  結果を𝕏でシェアする
                </Button>
                </div>
              </div>

              <h2 className="text-lg font-semibold">あなたのボドゲタイプは</h2>
              <p className="text-sm mb-2">{characterData.strengths}</p>
              <p
                className="text-3xl font-bold mb-2 px-4 py-1 inline-block rounded-[5px]"
                style={{ backgroundColor: characterData.color, color: "#fff" }}
              >
                {characterData.main_genre_jp}
              </p>
              <p className="text-sm text-gray-600 mb-6">{characterData.personality}</p>
              <p className="mt-6">あなたにピッタリのゲームを見る</p>
              <div className="text-xl animate-bounce mb-6">↓</div>

              <div
                className="w-full py-12 px-6 rounded-t-3xl"
                style={{ backgroundColor: characterData.color }}
              >
                <div className="flex justify-center gap-6">
                {filteredGames.map((game, index) => (
                <div key={index} className="bg-white rounded-xl p-4 w-72 shadow-md relative">
                  {/* ジャンル名ラベル */}
                  <div
                    className="absolute top-2 left-2 z-20 text-xs px-2 py-1 rounded-full text-white"
                    style={{ backgroundColor: characterData.color }}
                  >
                    {characterData.main_genre_jp}タイプ
                  </div>

                  {/* Amazonロゴ + クリップ */}
                  <div className="absolute top-2 right-2 z-30">
                    <div className="relative w-40 h-20">
                      {/* 背面のロゴ（白背景＋シャドウ弱め） */}
                      <div className="absolute top-[-10px] left-[-5px] w-full h-full bg-white p-1 rounded shadow z-0 transform rotate-[5deg] scale-30%">
                      <a
                        href={game.amazonLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full h-full flex items-center justify-center">
                        <img src={amazonLogo} alt="Amazon Logo" className="w-full h-full object-contain" />
                        </a>
                      </div>

                      {/* 前面のClipアイコン */}
                      <div className="absolute top-[-15px] right-[-15px] z-10">
                        <ClipIcon className="w-14 h-14 text-yellow-600" />
                      </div>
                    </div>
                  </div>


                  <img src={game.image} alt={game.name} className="w-full h-auto rounded mb-2 mt-6" />
                  <p className="text-sm text-gray-700 mb-1 font-semibold">難易度: {game.difficulty}</p>
                  <p className="text-xs text-gray-600 mb-3">{game.description}</p>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-800">
                    <div>👥 {game.minPlayers}〜{game.maxPlayers}人</div>
                    <div>⏱ {game.time}</div>
                    <div>🎯 {game.age}</div>
                    <div>
                      🏢 <a href={game.publisherLink} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">
                        {game.publisher}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
                </div>
              </div>

              {showSharePopup && characterData && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                  {/* ポップアップ本体 */}
                  <div className="bg-white bg-opacity-75 backdrop-blur-lg p-6 rounded-2xl max-w-md w-full text-center shadow-lg">
                    <p className="text-xl font-bold mb-4 text-[#2f2301]">
                      診断結果をシェアして<br />ボドゲ仲間を見つけよう！
                    </p>
                    <img
                      src={characterData.genre_ogp}
                      alt="診断OGP画像"
                      className="w-full rounded mb-4"
                    />
                    <p className="text-sm mb-4 text-[#2f2301]">
                      下のボタンをクリックすると𝕏の投稿下書きが作成されます。
                    </p>
                    <Button
                      onClick={() => {
                        const text = encodeURIComponent(
                          `【ボドゲ診断結果】\nわたしは${characterData.strengths}な「${characterData.main_genre_jp}」タイプです！\n#ボドゲ診断 #${characterData.main_genre_jp}タイプと繋がりたい`
                        );
                        const url = encodeURIComponent(`https://bodogeshindan.jp/g/${characterData.main_genre}.html`);
                        const ogpImage = encodeURIComponent(characterData.genre_ogp);
                        const shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}&hashtags=ボドゲ診断`;

                        window.open(shareUrl, "_blank");
                      }}
                      className="text-white text-sm px-6 py-3 rounded-full"
                      style={{ backgroundColor: characterData.color }}
                    >
                      結果を𝕏でシェアする
                    </Button>

                    <div className="mt-4">
                      <Button
                        onClick={() => setShowSharePopup(false)}
                        className="text-sm text-[#2f2301] underline"
                      >
                        閉じる
                      </Button>
                    </div>
                  </div>
                </div>
              )}

            </>
          ) : (
            <>
              <p className="mb-4">診断結果が見つかりませんでした。</p>
              <Button onClick={handleRetry} className="bg-[#2f2301] text-white rounded-full px-4 py-2 shadow-md">
                もう一度診断する
              </Button>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
